import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import api from '../config/api';
import { SingleImageUploader } from '../components/ImageUploader';
import PartnerPlanWall from '../components/PartnerPlanWall';
import PartnerLockup from '../components/PartnerLockup';
import PartnerLogoMark from '../components/PartnerLogoMark';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import PartnerWorkspaceFooter from '../components/PartnerWorkspaceFooter';
import WhatsAppOptInCard from '../components/WhatsAppOptInCard';
import { sellerReferenceLabel } from '../components/SellerDisclosures';
import { partnerPageInnerSx, partnerPageWrapSx } from '../components/PartnerListChrome';
import { DOCUMENT, documentFormLabelSx } from '../constants/documentTheme';
import { canInvitePartnerTeamMember, canUsePartnerWhiteLabel, formatPlanLimit, planNameKey } from '../utils/partnerPlan';

const SUPPORT_MAIL = 'partners@dejavunomad.com';

const SELLER_COUNTRY_OPTIONS = [
  ['US', 'United States'], ['AE', 'United Arab Emirates'], ['GB', 'United Kingdom'],
  ['CA', 'Canada'], ['MX', 'Mexico'], ['ES', 'Spain'], ['FR', 'France'],
  ['IT', 'Italy'], ['DE', 'Germany'], ['PT', 'Portugal'], ['GR', 'Greece'],
  ['NL', 'Netherlands'], ['CH', 'Switzerland'], ['AU', 'Australia'],
  ['NZ', 'New Zealand'], ['IN', 'India'], ['TH', 'Thailand'], ['ID', 'Indonesia'],
  ['MY', 'Malaysia'], ['VN', 'Vietnam'], ['SG', 'Singapore'], ['JP', 'Japan'],
  ['PE', 'Peru'], ['AR', 'Argentina'], ['CL', 'Chile'], ['CO', 'Colombia'],
  ['BR', 'Brazil'], ['CR', 'Costa Rica'], ['DO', 'Dominican Republic'],
  ['JM', 'Jamaica'], ['BS', 'Bahamas'], ['ZA', 'South Africa'], ['KE', 'Kenya'],
  ['MA', 'Morocco'], ['EG', 'Egypt'], ['TR', 'Türkiye'],
].map(([code, label]) => ({ code, label }));

const DIRECTORY_SPECIALTIES = [
  'luxury',
  'groups',
  'honeymoons',
  'family',
  'adventure',
  'cruises',
  'corporate',
  'wellness',
  'safari',
  'cultural',
];

const parsePartnerSpecialties = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const directoryTierKey = (tier) => {
  switch (tier) {
    case 'pro':
      return 'verified';
    case 'group':
      return 'priority';
    case 'agency':
      return 'featured';
    case 'bespoke':
      return 'enterprise';
    default:
      return 'basic';
  }
};

const emptyBrandingForm = {
  logo: '',
  brandColor: '',
  brandBanner: '',
  brandNavInk: '',
  brandInk: '',
  brandCanvas: '',
  brandReplyTo: '',
  brandSenderDomain: '',
};

const brandingFromPartner = (next) => ({
  logo: next?.logo || '',
  brandColor: next?.brand_color || '',
  brandBanner: next?.brand_banner || '',
  brandNavInk: next?.brand_nav_ink || '',
  brandInk: next?.brand_ink || '',
  brandCanvas: next?.brand_canvas || '',
  brandReplyTo: next?.brand_reply_to || '',
  brandSenderDomain: next?.brand_sender_domain || '',
});

const linkSx = {
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  textDecoration: 'none',
  borderBottom: '1px solid transparent',
  pb: '2px',
  cursor: 'pointer',
  background: 'none',
  border: 0,
  p: 0,
  fontFamily: 'inherit',
  '&:hover': { color: DOCUMENT.ink, borderBottomColor: DOCUMENT.accent },
};

const fieldDtSx = {
  ...documentFormLabelSx,
  mb: 0.75,
};

const fieldDdSx = {
  fontSize: 15,
  lineHeight: 1.5,
  color: DOCUMENT.ink,
  m: 0,
};

const Field = ({ label, children, wide = false, numeric = false }) => (
  <Box
    sx={{
      py: 2,
      borderBottom: `1px solid ${DOCUMENT.rule}`,
      gridColumn: wide ? '1 / -1' : 'auto',
    }}
  >
    <Typography component="dt" sx={fieldDtSx}>{label}</Typography>
    <Typography component="dd" sx={{ ...fieldDdSx, fontVariantNumeric: numeric ? 'tabular-nums' : 'normal' }}>
      {children}
    </Typography>
  </Box>
);

const usagePercent = (used, limit) => {
  if (limit == null || limit < 0) return 0;
  if (limit === 0) return 0;
  return Math.min(100, Math.round((Number(used) || 0) / limit * 100));
};

const websiteHref = (website) => {
  if (!website) return null;
  return website.startsWith('http') ? website : `https://${website}`;
};

const websiteLabel = (website) => {
  if (!website) return null;
  return website.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

const parseDate = (value) => {
  if (!value) return null;
  const parsed = parseISO(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const PartnerProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [partner, setPartner] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [planWallReason, setPlanWallReason] = useState('billing');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [directoryForm, setDirectoryForm] = useState({
    directoryListed: false,
    description: '',
    locationCity: '',
    locationRegion: '',
    locationCountry: '',
    specialties: [],
  });
  const [directorySaving, setDirectorySaving] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [directorySuccess, setDirectorySuccess] = useState('');
  const [editFormData, setEditFormData] = useState({
    companyName: '',
    contactName: '',
    companyAddress: '',
    phoneNumber: '',
    businessType: '',
    businessTypeOther: '',
    website: '',
    logo: '',
    avatar: '',
    brandColor: '',
    brandBanner: '',
    brandNavInk: '',
    brandInk: '',
    brandCanvas: '',
    brandReplyTo: '',
    brandSenderDomain: '',
  });
  const [brandingForm, setBrandingForm] = useState(emptyBrandingForm);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingError, setBrandingError] = useState('');
  const [brandingSuccess, setBrandingSuccess] = useState('');
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [sellerForm, setSellerForm] = useState({
    sellerCountry: '',
    sellerReferenceNumber: '',
    sellerReferenceRegion: 'CA',
    sellerFundsProtection: '',
    sellerTcrfParticipant: false,
  });
  const [sellerSaving, setSellerSaving] = useState(false);
  const [sellerError, setSellerError] = useState('');
  const [sellerSuccess, setSellerSuccess] = useState('');

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem('partnerToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAll = async () => {
    const token = localStorage.getItem('partnerToken');
    if (!token) {
      navigate('/partner/login');
      return;
    }

    try {
      setLoading(true);
      await Promise.all([fetchPartnerProfile(), fetchPartnerPlan(), fetchTeam()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerProfile = async () => {
    try {
      const response = await api.get('/partners/profile', {
        headers: authHeaders(),
      });
      setPartner(response.data.partner);
      setSellerForm({
        sellerCountry: response.data.partner.location_country || '',
        sellerReferenceNumber: response.data.partner.seller_reference_number || '',
        sellerReferenceRegion: response.data.partner.seller_reference_region || 'CA',
        sellerFundsProtection: response.data.partner.seller_funds_protection || '',
        sellerTcrfParticipant: Boolean(response.data.partner.seller_tcrf_participant),
      });
      setBrandingForm(brandingFromPartner(response.data.partner));
      setDirectoryForm({
        directoryListed: Boolean(response.data.partner.directory_listed),
        description: response.data.partner.description || '',
        locationCity: response.data.partner.location_city || '',
        locationRegion: response.data.partner.location_region || '',
        locationCountry: response.data.partner.location_country || '',
        specialties: parsePartnerSpecialties(response.data.partner.specialties),
      });
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
    } catch (err) {
      console.error('Error fetching partner profile:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('partnerToken');
        localStorage.removeItem('partner');
        navigate('/partner/login');
        return;
      }
      setError(t('partnerProfile.loadError'));
    }
  };

  const fetchPartnerPlan = async () => {
    try {
      const response = await api.get('/partners/plan');
      setEntitlements(response.data.entitlements);
    } catch (err) {
      console.error('Error fetching partner plan:', err);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await api.get('/partner-users', {
        headers: authHeaders(),
      });
      setTeam(response.data.users || []);
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };

  const canEdit = partner?.role === 'owner';
  const canEditBranding = canEdit && canUsePartnerWhiteLabel(entitlements);

  const toggleDirectorySpecialty = (value) => {
    setDirectoryForm((prev) => {
      const has = prev.specialties.includes(value);
      const specialties = has
        ? prev.specialties.filter((item) => item !== value)
        : [...prev.specialties, value].slice(0, 12);
      return { ...prev, specialties };
    });
  };

  const saveDirectoryListing = async () => {
    try {
      setDirectorySaving(true);
      setDirectoryError('');
      setDirectorySuccess('');
      const response = await api.patch('/partners/profile/directory', {
        directoryListed: directoryForm.directoryListed,
        description: directoryForm.description,
        locationCity: directoryForm.locationCity,
        locationRegion: directoryForm.locationRegion,
        locationCountry: directoryForm.locationCountry,
        specialties: directoryForm.specialties,
      }, { headers: authHeaders() });
      if (response.data?.directory) {
        setDirectoryForm((prev) => ({
          ...prev,
          directoryListed: response.data.directory.directoryListed,
          description: response.data.directory.description || '',
          locationCity: response.data.directory.locationCity || '',
          locationRegion: response.data.directory.locationRegion || '',
          locationCountry: response.data.directory.locationCountry || '',
          specialties: response.data.directory.specialties || [],
        }));
      }
      setDirectorySuccess(t('partnerProfile.directory.saved'));
      await fetchPartnerProfile();
    } catch (err) {
      setDirectoryError(err.response?.data?.error || t('partnerProfile.directory.saveError'));
    } finally {
      setDirectorySaving(false);
    }
  };

  const directoryPreviewSlug = partner?.directory_slug;

  const persistAvatar = async (url) => {
    if (!partner) return;
    setAvatarSaving(true);
    setError('');
    try {
      const payload = canEdit
        ? {
          companyName: partner.company_name,
          contactName: partner.contact_name,
          companyAddress: partner.company_address,
          phoneNumber: partner.phone_number,
          businessType: partner.business_type,
          businessTypeOther: partner.business_type_other,
          website: partner.website,
          logo: partner.logo,
          avatar: url || '',
        }
        : { avatar: url || '' };
      const response = await api.put('/partners/profile', payload, {
        headers: authHeaders(),
        usePartnerAuth: true,
      });
      setPartner(response.data.partner);
      setEditFormData((prev) => ({ ...prev, avatar: response.data.partner?.avatar || '' }));
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerProfile.updateError'));
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleEditOpen = () => {
    setEditFormData({
      companyName: partner?.company_name || '',
      contactName: partner?.contact_name || '',
      companyAddress: partner?.company_address || '',
      phoneNumber: partner?.phone_number || '',
      businessType: partner?.business_type || '',
      businessTypeOther: partner?.business_type_other || '',
      website: partner?.website || '',
      logo: partner?.logo || '',
      avatar: partner?.avatar || '',
      brandColor: partner?.brand_color || '',
      brandBanner: partner?.brand_banner || '',
      brandNavInk: partner?.brand_nav_ink || '',
      brandInk: partner?.brand_ink || '',
      brandCanvas: partner?.brand_canvas || '',
      brandReplyTo: partner?.brand_reply_to || '',
      brandSenderDomain: partner?.brand_sender_domain || '',
    });
    setEditError('');
    setEditSuccess('');
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditError('');
    setEditSuccess('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBrandingChange = (e) => {
    const { name, value } = e.target;
    setBrandingForm((prev) => ({ ...prev, [name]: value }));
    setBrandingSuccess('');
  };

  const handleBrandingSave = async (e) => {
    e.preventDefault();
    if (!partner) return;
    setBrandingSaving(true);
    setBrandingError('');
    setBrandingSuccess('');

    try {
      const response = await api.put('/partners/profile', {
        companyName: partner.company_name,
        contactName: partner.contact_name,
        companyAddress: partner.company_address,
        phoneNumber: partner.phone_number,
        businessType: partner.business_type,
        businessTypeOther: partner.business_type_other,
        website: partner.website,
        ...brandingForm,
        logo: brandingForm.logo,
      }, {
        headers: authHeaders(),
        usePartnerAuth: true,
      });

      setPartner(response.data.partner);
      setBrandingForm(brandingFromPartner(response.data.partner));
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
      setBrandingSuccess(t('partnerProfile.themeSaved'));
    } catch (err) {
      setBrandingError(err.response?.data?.error || t('partnerProfile.updateError'));
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleSellerSave = async (e) => {
    e.preventDefault();
    if (!partner) return;
    setSellerSaving(true);
    setSellerError('');
    setSellerSuccess('');
    try {
      const response = await api.put('/partners/profile', {
        companyName: partner.company_name,
        contactName: partner.contact_name,
        companyAddress: partner.company_address,
        phoneNumber: partner.phone_number,
        businessType: partner.business_type,
        businessTypeOther: partner.business_type_other,
        website: partner.website,
        logo: partner.logo,
        locationCountry: sellerForm.sellerCountry || '',
        ...sellerForm,
      }, {
        headers: authHeaders(),
        usePartnerAuth: true,
      });
      setPartner(response.data.partner);
      setSellerForm({
        sellerCountry: response.data.partner.location_country || '',
        sellerReferenceNumber: response.data.partner.seller_reference_number || '',
        sellerReferenceRegion: response.data.partner.seller_reference_region || 'CA',
        sellerFundsProtection: response.data.partner.seller_funds_protection || '',
        sellerTcrfParticipant: Boolean(response.data.partner.seller_tcrf_participant),
      });
      setDirectoryForm((prev) => ({
        ...prev,
        locationCountry: response.data.partner.location_country || prev.locationCountry,
      }));
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
      setSellerSuccess(t('partnerProfile.updated'));
    } catch (err) {
      setSellerError(err.response?.data?.error || t('partnerProfile.updateError'));
    } finally {
      setSellerSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');

    try {
      const response = await api.put('/partners/profile', {
        companyName: editFormData.companyName,
        contactName: editFormData.contactName,
        companyAddress: editFormData.companyAddress,
        phoneNumber: editFormData.phoneNumber,
        businessType: editFormData.businessType,
        businessTypeOther: editFormData.businessTypeOther,
        website: editFormData.website,
        ...(canEditBranding ? brandingForm : {}),
        logo: brandingForm.logo || partner?.logo || '',
        avatar: editFormData.avatar,
      }, {
        headers: authHeaders(),
        usePartnerAuth: true,
      });

      setPartner(response.data.partner);
      setBrandingForm(brandingFromPartner(response.data.partner));
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
      setEditSuccess(t('partnerProfile.updated'));
      setTimeout(() => handleEditClose(), 1200);
    } catch (err) {
      console.error('Error updating profile:', err);
      setEditError(err.response?.data?.error || t('partnerProfile.updateError'));
    } finally {
      setEditLoading(false);
    }
  };

  const businessTypeLabel = (type) => {
    if (!type) return t('partnerProfile.notProvided');
    return t(`partnerDash.business.${type}`, partner?.business_type_other || type);
  };

  const emptyValue = (
    <Box component="span" sx={{ color: DOCUMENT.ink3 }}>{t('partnerProfile.notProvided')}</Box>
  );

  const lastSeenLabel = (user) => {
    if (!user.is_active) return t('partnerProfile.inactive');
    const login = parseDate(user.last_login);
    if (!login) return t('partnerProfile.neverSeen');
    const minutes = (Date.now() - login.getTime()) / 60000;
    if (minutes < 15) return t('partnerProfile.activeNow');
    return t('partnerProfile.seenWhen', { when: formatDistanceToNow(login) });
  };

  const periodEnd = parseDate(entitlements?.currentPeriodEnd);
  const clientUsed = entitlements?.usage?.clientTrips ?? 0;
  const groupUsed = entitlements?.usage?.groupTrips ?? 0;
  const teamUsed = entitlements?.usage?.teamMembers ?? team.length;
  const clientLimit = entitlements?.limits?.maxClientTrips;
  const groupLimit = entitlements?.limits?.maxGroupTrips;
  const teamLimit = entitlements?.limits?.includedTeamSeats ?? entitlements?.limits?.maxTeamMembers;

  const usageCards = [
    {
      label: t('partnerProfile.clientTrips'),
      used: clientUsed,
      limit: clientLimit,
      percent: usagePercent(clientUsed, clientLimit),
    },
    {
      label: t('partnerProfile.groupTrips'),
      used: groupUsed,
      limit: groupLimit,
      percent: usagePercent(groupUsed, groupLimit),
    },
    {
      label: t('partnerProfile.teamMembers'),
      used: teamUsed,
      limit: teamLimit,
      percent: usagePercent(teamUsed, teamLimit),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ bgcolor: DOCUMENT.canvas, py: 8, minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: DOCUMENT.canvas, py: 8, px: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={partnerPageWrapSx}>
      <Box sx={partnerPageInnerSx}>
        <Typography sx={{ pt: { xs: 4.25, md: 4.25 }, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
          <Box
            component={RouterLink}
            to="/partner/dashboard"
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: DOCUMENT.ink } }}
          >
            {t('partnerProfile.crumbOverview')}
          </Box>
          {' · '}
          {t('partnerProfile.crumbProfile')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 2.5,
            flexWrap: 'wrap',
            mt: 1.75,
            pb: 2,
            borderBottom: `1px solid ${DOCUMENT.ink}`,
          }}
        >
          <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 30, md: 42 }, fontWeight: 400, lineHeight: 1 }}>
            {t('partnerProfile.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, pb: 0.6 }}>
            {t('partnerProfile.subtitle')}
          </Typography>
        </Box>

        <Section
          title={t('partnerProfile.business')}
          action={canEdit ? t('partnerProfile.edit') : null}
          onAction={handleEditOpen}
        >
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 2.25, md: 3.25 },
              alignItems: 'flex-start',
              py: 2.75,
              borderBottom: `1px solid ${DOCUMENT.rule}`,
            }}
          >
            {isUsableImageUrl(partner?.logo) ? (
              <PartnerLockup
                src={partner.logo}
                alt={partner?.company_name || ''}
                banner={partner?.brand_banner}
                height={48}
                maxWidth={280}
                sx={{ px: 1.5, py: 1 }}
              />
            ) : (
              <PartnerLogoMark
                partner={partner}
                alt={partner?.company_name || ''}
                hideOnError
                sx={{
                  width: 'auto',
                  height: { xs: 48, md: 64 },
                  maxWidth: { xs: 160, md: 220 },
                  flexShrink: 0,
                  objectFit: 'contain',
                }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 22, md: 27 }, fontWeight: 400, lineHeight: 1.1 }}>
                {partner?.company_name}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.85, letterSpacing: '0.03em' }}>
                {businessTypeLabel(partner?.business_type)}
              </Typography>
            </Box>
          </Box>
          <Fields>
            <Field label={t('partnerProfile.tradingName')}>{partner?.company_name || emptyValue}</Field>
            <Field label={t('partnerProfile.businessType')}>{businessTypeLabel(partner?.business_type)}</Field>
            <Field label={t('partnerProfile.website')}>
              {partner?.website ? (
                <Box
                  component="a"
                  href={websiteHref(partner.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: DOCUMENT.ink, textDecoration: 'none', borderBottom: `1px solid ${DOCUMENT.ruleHard}`, '&:hover': { borderBottomColor: DOCUMENT.accent } }}
                >
                  {websiteLabel(partner.website)}
                </Box>
              ) : emptyValue}
            </Field>
            <Field label={t('partnerProfile.founded')} numeric>{emptyValue}</Field>
          </Fields>
        </Section>

        <Section
          title={t('partnerProfile.contact')}
          action={canEdit ? t('partnerProfile.edit') : null}
          onAction={handleEditOpen}
        >
          <Box sx={{ pt: 2.5, pb: 1 }}>
            <SingleImageUploader
              value={partner?.avatar || ''}
              onChange={(url) => persistAvatar(url || '')}
              preset="thumbnail"
              folder="partners/avatars"
              label={t('partnerProfile.profilePhoto')}
              height={112}
              cropAspect={1}
              cropShape="round"
              usePartnerAuth
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('partnerProfile.profilePhotoHint')}
            </Typography>
            {avatarSaving && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('partnerProfile.photoSaving')}
              </Typography>
            )}
          </Box>
          <Fields>
            <Field label={t('partnerProfile.primaryContact')}>{partner?.contact_name || emptyValue}</Field>
            <Field label={t('partnerProfile.role')}>
              {t(`partnerProfile.roles.${partner?.role || 'owner'}`, partner?.role || 'owner')}
            </Field>
            <Field label={t('partnerProfile.email')}>
              {partner?.email ? (
                <Box
                  component="a"
                  href={`mailto:${partner.email}`}
                  sx={{ color: DOCUMENT.ink, textDecoration: 'none', borderBottom: `1px solid ${DOCUMENT.ruleHard}`, '&:hover': { borderBottomColor: DOCUMENT.accent } }}
                >
                  {partner.email}
                </Box>
              ) : emptyValue}
            </Field>
            <Field label={t('partnerProfile.phone')} numeric>{partner?.phone_number || emptyValue}</Field>
          </Fields>
        </Section>

        <WhatsAppOptInCard mode="partner" />

        <Section title={t('sellerDisclosures.profileTitle')}>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, pt: 2, pb: 1.5 }}>
            {t('sellerDisclosures.profileHelp')}
          </Typography>
          {!partner?.seller_reference_number ? (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="seller-reference-missing">
              <strong>{t('sellerDisclosures.missingTitle')}:</strong> {t('sellerDisclosures.missingBody')}
            </Alert>
          ) : null}
          {sellerError ? <Alert severity="error" sx={{ mb: 2 }}>{sellerError}</Alert> : null}
          {sellerSuccess ? <Alert severity="success" sx={{ mb: 2 }}>{sellerSuccess}</Alert> : null}
          {canEdit ? (
            <Box component="form" onSubmit={handleSellerSave} data-testid="partner-seller-editor">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={SELLER_COUNTRY_OPTIONS}
                    value={SELLER_COUNTRY_OPTIONS.find((option) => option.code === sellerForm.sellerCountry)
                      || (sellerForm.sellerCountry
                        ? { code: sellerForm.sellerCountry, label: sellerForm.sellerCountry }
                        : null)}
                    onChange={(event, option) => {
                      const country = option?.code || '';
                      setSellerForm((prev) => ({ ...prev, sellerCountry: country }));
                      setDirectoryForm((prev) => ({ ...prev, locationCountry: country }));
                    }}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        bgcolor: DOCUMENT.canvas,
                        fontFamily: DOCUMENT.sans,
                        fontSize: 14,
                        color: DOCUMENT.ink,
                      },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: DOCUMENT.ruleHard },
                      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: DOCUMENT.accent,
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: DOCUMENT.oxblood,
                        borderWidth: 1,
                      },
                      '& .MuiInputLabel-root': { fontFamily: DOCUMENT.sans, color: DOCUMENT.ink3 },
                      '& .MuiInputLabel-root.Mui-focused': { color: DOCUMENT.oxblood },
                    }}
                    componentsProps={{
                      paper: {
                        sx: {
                          borderRadius: 0,
                          border: `1px solid ${DOCUMENT.rule}`,
                          bgcolor: DOCUMENT.canvas,
                          boxShadow: '0 10px 28px rgba(26, 14, 18, 0.14)',
                          mt: 0.5,
                        },
                      },
                      listbox: {
                        sx: {
                          maxHeight: 240,
                          fontSize: 13,
                          fontFamily: DOCUMENT.sans,
                          color: DOCUMENT.ink,
                          p: 0.5,
                          '& .MuiAutocomplete-option': {
                            minHeight: 32,
                            borderRadius: 0,
                          },
                          '& .MuiAutocomplete-option[aria-selected="true"]': {
                            bgcolor: DOCUMENT.ivory,
                            color: DOCUMENT.oxblood,
                            fontWeight: 600,
                          },
                          '& .MuiAutocomplete-option.Mui-focused': {
                            bgcolor: DOCUMENT.ivory,
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('sellerDisclosures.sellerCountry')}
                        inputProps={{ ...params.inputProps, 'data-testid': 'seller-country' }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              {!sellerForm.sellerCountry ? (
                <Alert severity="info" sx={{ mt: 2 }} data-testid="seller-country-first">
                  {t('sellerDisclosures.sellerCountryFirst')}
                </Alert>
              ) : (
              <Grid container spacing={2} sx={{ mt: 0 }}>
                {(sellerForm.sellerCountry || '').toUpperCase() === 'US' ? (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label={t('sellerDisclosures.referenceRegion')}
                      value={sellerForm.sellerReferenceRegion || 'CA'}
                      onChange={(event) => setSellerForm((prev) => ({
                        ...prev,
                        sellerReferenceRegion: event.target.value,
                      }))}
                      inputProps={{ 'data-testid': 'seller-reference-region' }}
                    >
                      <MenuItem value="CA">{t('sellerDisclosures.regionCa')}</MenuItem>
                      <MenuItem value="FL">{t('sellerDisclosures.regionFl')}</MenuItem>
                      <MenuItem value="HI">{t('sellerDisclosures.regionHi')}</MenuItem>
                      <MenuItem value="WA">{t('sellerDisclosures.regionWa')}</MenuItem>
                      <MenuItem value="OT">{t('sellerDisclosures.regionOtherUs')}</MenuItem>
                    </TextField>
                  </Grid>
                ) : null}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={(() => {
                      const isUsSeller = (sellerForm.sellerCountry || '').toUpperCase() === 'US';
                      if (!isUsSeller) return t('sellerDisclosures.referenceLicense');
                      if (sellerForm.sellerReferenceRegion === 'FL') return t('sellerDisclosures.referenceFl');
                      if (sellerForm.sellerReferenceRegion === 'HI') return t('sellerDisclosures.referenceHi');
                      if (sellerForm.sellerReferenceRegion === 'WA') return t('sellerDisclosures.referenceWa');
                      if (sellerForm.sellerReferenceRegion === 'OT') return t('sellerDisclosures.referenceUsOther');
                      return t('sellerDisclosures.referenceCst');
                    })()}
                    value={sellerForm.sellerReferenceNumber}
                    onChange={(event) => setSellerForm((prev) => ({
                      ...prev,
                      sellerReferenceNumber: event.target.value,
                    }))}
                    inputProps={{ 'data-testid': 'seller-reference-number' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('sellerDisclosures.fundsProtection')}
                    value={sellerForm.sellerFundsProtection}
                    onChange={(event) => setSellerForm((prev) => ({
                      ...prev,
                      sellerFundsProtection: event.target.value,
                    }))}
                  >
                    <MenuItem value="">{t('sellerDisclosures.fundsNone')}</MenuItem>
                    <MenuItem value="trust_account">{t('sellerDisclosures.fundsTrustAccount')}</MenuItem>
                    <MenuItem value="bond">{t('sellerDisclosures.fundsBond')}</MenuItem>
                  </TextField>
                </Grid>
                {(sellerForm.sellerCountry || '').toUpperCase() === 'US'
                  && (sellerForm.sellerReferenceRegion || 'CA') === 'CA' ? (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={sellerForm.sellerTcrfParticipant}
                          onChange={(event) => setSellerForm((prev) => ({
                            ...prev,
                            sellerTcrfParticipant: event.target.checked,
                          }))}
                          color="default"
                        />
                      )}
                      label={t('sellerDisclosures.tcrfParticipant')}
                    />
                  </Grid>
                ) : null}
              </Grid>
              )}
              <Box sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" disabled={sellerSaving || !sellerForm.sellerCountry} data-testid="seller-reference-save">
                  {sellerSaving ? <CircularProgress size={22} /> : t('common.save')}
                </Button>
              </Box>
            </Box>
          ) : (
            <Field label={t('sellerDisclosures.referenceCst')}>
              {partner?.seller_reference_number || emptyValue}
            </Field>
          )}
        </Section>

        <Section title={t('partnerProfile.directory.title')}>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, pt: 2, pb: 1.5 }}>
            {t('partnerProfile.directory.help')}
          </Typography>
          {entitlements ? (
            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2 }}>
              {t(`partnerProfile.directory.tierBenefit.${directoryTierKey(entitlements.tier)}`)}
            </Typography>
          ) : null}
          {directoryError ? <Alert severity="error" sx={{ mb: 2 }}>{directoryError}</Alert> : null}
          {directorySuccess ? <Alert severity="success" sx={{ mb: 2 }}>{directorySuccess}</Alert> : null}
          {canEdit ? (
            <Box component="form" onSubmit={(event) => { event.preventDefault(); saveDirectoryListing(); }} data-testid="partner-directory-editor">
              <FormControlLabel
                control={
                  <Switch
                    checked={directoryForm.directoryListed}
                    onChange={(event) => setDirectoryForm((prev) => ({
                      ...prev,
                      directoryListed: event.target.checked,
                    }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: DOCUMENT.ivory,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: DOCUMENT.oxblood,
                        opacity: 1,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: DOCUMENT.ruleHard,
                        opacity: 1,
                      },
                    }}
                  />
                }
                label={t('partnerProfile.directory.listedLabel')}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={t('partnerProfile.directory.description')}
                    value={directoryForm.description}
                    onChange={(event) => setDirectoryForm((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('partnerProfile.directory.city')}
                    value={directoryForm.locationCity}
                    onChange={(event) => setDirectoryForm((prev) => ({
                      ...prev,
                      locationCity: event.target.value,
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('partnerProfile.directory.region')}
                    value={directoryForm.locationRegion}
                    onChange={(event) => setDirectoryForm((prev) => ({
                      ...prev,
                      locationRegion: event.target.value,
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('partnerProfile.directory.country')}
                    value={directoryForm.locationCountry}
                    onChange={(event) => setDirectoryForm((prev) => ({
                      ...prev,
                      locationCountry: event.target.value,
                    }))}
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mb: 1 }}>
                    {t('partnerProfile.directory.specialtiesLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {DIRECTORY_SPECIALTIES.map((item) => (
                      <Chip
                        key={item}
                        label={t(`advisorDirectory.specialties.${item}`)}
                        onClick={() => toggleDirectorySpecialty(item)}
                        sx={{
                          borderRadius: 0,
                          bgcolor: directoryForm.specialties.includes(item) ? DOCUMENT.oxblood : DOCUMENT.canvas,
                          color: directoryForm.specialties.includes(item) ? DOCUMENT.ivory : DOCUMENT.ink,
                          border: `1px solid ${DOCUMENT.rule}`,
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
              {directoryPreviewSlug && directoryForm.directoryListed ? (
                <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 2 }}>
                  {t('partnerProfile.directory.preview')}{' '}
                  <Box
                    component={RouterLink}
                    to={`/advisors/${directoryPreviewSlug}`}
                    sx={{ color: DOCUMENT.oxblood, fontWeight: 600 }}
                  >
                    /advisors/{directoryPreviewSlug}
                  </Box>
                </Typography>
              ) : null}
              <Button
                type="submit"
                disabled={directorySaving}
                sx={{
                  mt: 2.5,
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  borderRadius: 0,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
                }}
              >
                {directorySaving ? <CircularProgress size={22} /> : t('partnerProfile.directory.save')}
              </Button>
            </Box>
          ) : (
            <Fields>
              <Field label={t('partnerProfile.directory.listedLabel')}>
                {directoryForm.directoryListed ? t('partnerProfile.directory.listedYes') : t('partnerProfile.directory.listedNo')}
              </Field>
              <Field label={t('partnerProfile.directory.description')} wide>
                {directoryForm.description || emptyValue}
              </Field>
            </Fields>
          )}
        </Section>

        <Section title={t('partnerProfile.branding')}>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, pt: 2, pb: 1.5 }}>
            {t('partnerProfile.brandingHelp')}
          </Typography>
          {canEdit && !canUsePartnerWhiteLabel(entitlements) && (
            <Box component="form" onSubmit={handleBrandingSave} data-testid="partner-logo-editor">
              {brandingError && <Alert severity="error" sx={{ mb: 2 }}>{brandingError}</Alert>}
              {brandingSuccess && <Alert severity="success" sx={{ mb: 2 }}>{brandingSuccess}</Alert>}
              <Box sx={{ mb: 1 }}>
                <SingleImageUploader
                  value={brandingForm.logo}
                  onChange={(url) => {
                    setBrandingForm((prev) => ({ ...prev, logo: url || '' }));
                    setEditFormData((prev) => ({ ...prev, logo: url || '' }));
                    setBrandingSuccess('');
                  }}
                  preset="gallery"
                  folder="partners/logos"
                  label={t('partnerProfile.companyLogo')}
                  height={120}
                  cropAspect={null}
                  usePartnerAuth
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('partnerProfile.logoHint')}
                </Typography>
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={brandingSaving}
                sx={{
                  mt: 1.5,
                  mb: 1,
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  borderRadius: 0,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
                }}
              >
                {brandingSaving ? <CircularProgress size={22} /> : t('partnerProfile.saveLogo')}
              </Button>
            </Box>
          )}
          {canUsePartnerWhiteLabel(entitlements) ? (
            canEditBranding ? (
              <Box component="form" onSubmit={handleBrandingSave} data-testid="partner-theme-editor">
                {brandingError && <Alert severity="error" sx={{ mb: 2 }}>{brandingError}</Alert>}
                {brandingSuccess && <Alert severity="success" sx={{ mb: 2 }}>{brandingSuccess}</Alert>}
                <ThemePreview form={brandingForm} />
                <Box sx={{ mt: 2.5, mb: 1 }}>
                  <SingleImageUploader
                    value={brandingForm.logo}
                    onChange={(url) => {
                      setBrandingForm((prev) => ({ ...prev, logo: url || '' }));
                      setEditFormData((prev) => ({ ...prev, logo: url || '' }));
                      setBrandingSuccess('');
                    }}
                    preset="gallery"
                    folder="partners/logos"
                    label={t('partnerProfile.companyLogo')}
                    height={120}
                    cropAspect={null}
                    usePartnerAuth
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {t('partnerProfile.logoHint')}
                  </Typography>
                </Box>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <ColorField
                      name="brandBanner"
                      label={t('partnerProfile.brandBanner')}
                      hint={t('partnerProfile.brandBannerHint')}
                      value={brandingForm.brandBanner}
                      fallback="#000000"
                      disabled={brandingSaving}
                      onChange={handleBrandingChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorField
                      name="brandNavInk"
                      label={t('partnerProfile.brandMenu')}
                      hint={t('partnerProfile.brandMenuHint')}
                      value={brandingForm.brandNavInk}
                      fallback="#F6EFE4"
                      disabled={brandingSaving}
                      onChange={handleBrandingChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorField
                      name="brandInk"
                      label={t('partnerProfile.brandInk')}
                      hint={t('partnerProfile.brandInkHint')}
                      value={brandingForm.brandInk}
                      fallback="#000000"
                      disabled={brandingSaving}
                      onChange={handleBrandingChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorField
                      name="brandCanvas"
                      label={t('partnerProfile.brandCanvas')}
                      hint={t('partnerProfile.brandCanvasHint')}
                      value={brandingForm.brandCanvas}
                      fallback="#FFFFFF"
                      disabled={brandingSaving}
                      onChange={handleBrandingChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorField
                      name="brandColor"
                      label={t('partnerProfile.brandColor')}
                      hint={t('partnerProfile.brandColorHint')}
                      value={brandingForm.brandColor}
                      fallback="#C78E33"
                      disabled={brandingSaving}
                      onChange={handleBrandingChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('partnerProfile.brandReplyTo')}
                      name="brandReplyTo"
                      type="email"
                      value={brandingForm.brandReplyTo}
                      onChange={handleBrandingChange}
                      disabled={brandingSaving}
                      helperText={t('partnerProfile.brandReplyToHint')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('partnerProfile.brandSenderDomain')}
                      name="brandSenderDomain"
                      value={brandingForm.brandSenderDomain}
                      onChange={handleBrandingChange}
                      disabled={brandingSaving}
                      placeholder="blackopallife.com"
                      helperText={t('partnerProfile.brandSenderDomainHint')}
                    />
                  </Grid>
                </Grid>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={brandingSaving}
                  sx={{
                    mt: 2.5,
                    bgcolor: DOCUMENT.oxblood,
                    color: DOCUMENT.ivory,
                    borderRadius: 0,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: DOCUMENT.nav, boxShadow: 'none' },
                  }}
                >
                  {brandingSaving ? <CircularProgress size={22} /> : t('partnerProfile.saveTheme')}
                </Button>
              </Box>
            ) : (
              <Fields>
                <Field label={t('partnerProfile.brandBanner')} numeric>
                  <Swatch value={partner?.brand_banner} empty={emptyValue} />
                </Field>
                <Field label={t('partnerProfile.brandMenu')} numeric>
                  <Swatch value={partner?.brand_nav_ink} empty={emptyValue} />
                </Field>
                <Field label={t('partnerProfile.brandInk')} numeric>
                  <Swatch value={partner?.brand_ink} empty={emptyValue} />
                </Field>
                <Field label={t('partnerProfile.brandCanvas')} numeric>
                  <Swatch value={partner?.brand_canvas} empty={emptyValue} />
                </Field>
                <Field label={t('partnerProfile.brandColor')} numeric>
                  <Swatch value={partner?.brand_color} empty={emptyValue} />
                </Field>
                <Field label={t('partnerProfile.brandReplyTo')}>
                  {partner?.brand_reply_to || emptyValue}
                </Field>
                <Field label={t('partnerProfile.brandSenderDomain')}>
                  {partner?.brand_sender_domain || emptyValue}
                </Field>
              </Fields>
            )
          ) : (
            <Typography sx={{ py: 2.5, fontSize: 13.5, color: DOCUMENT.ink3 }}>
              {t('partnerProfile.brandingBespokeOnly')}
            </Typography>
          )}
        </Section>

        <Section
          title={t('partnerProfile.registered')}
          action={canEdit ? t('partnerProfile.edit') : null}
          onAction={handleEditOpen}
        >
          <Fields>
            <Field label={t('partnerProfile.registeredAddress')} wide>
              {partner?.company_address ? (
                <Box sx={{ whiteSpace: 'pre-line' }}>{partner.company_address}</Box>
              ) : emptyValue}
            </Field>
            <Field label={t('partnerProfile.registrationNumber')} numeric>
              {partner?.business_registration_number || emptyValue}
            </Field>
            <Field label={t('partnerProfile.taxId')} numeric>{emptyValue}</Field>
            <Field label={t('partnerProfile.sellerLicence')}>
              {partner?.seller_reference_number
                ? `${sellerReferenceLabel(t, {
                  referenceKind: partner.seller_reference_kind,
                  referenceRegion: partner.seller_reference_region,
                })} ${partner.seller_reference_number}`
                : emptyValue}
            </Field>
            <Field label={t('partnerProfile.liabilityInsurance')}>{emptyValue}</Field>
          </Fields>
        </Section>

        <Section title={t('partnerProfile.planUsage')}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              py: 2.25,
              borderBottom: `1px solid ${DOCUMENT.rule}`,
            }}
          >
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, fontWeight: 400 }}>
              {entitlements ? t(planNameKey(entitlements.tier)) : t('partnerPlans.starter')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
              {periodEnd
                ? t('partnerProfile.renews', { date: format(periodEnd, 'd MMMM yyyy') })
                : t('partnerProfile.noRenewal')}
            </Typography>
            <Button
              onClick={() => {
                setPlanWallReason('billing');
                setPlanWallOpen(true);
              }}
              sx={{
                ml: { sm: 'auto' },
                bgcolor: DOCUMENT.oxblood,
                color: DOCUMENT.ivory,
                borderRadius: 0,
                px: 2.25,
                py: 1.15,
                fontSize: 11,
                letterSpacing: '0.11em',
                textTransform: 'uppercase',
                '&:hover': { bgcolor: DOCUMENT.nav },
              }}
            >
              {t('partnerProfile.changePlan')}
            </Button>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: '1px',
              bgcolor: DOCUMENT.ruleHard,
              border: `1px solid ${DOCUMENT.ruleHard}`,
              mt: 2.25,
            }}
          >
            {usageCards.map((card) => (
              <Box key={card.label} sx={{ bgcolor: DOCUMENT.surface, px: 2.5, pt: 2.25, pb: 2 }}>
                <Typography sx={fieldDtSx}>{card.label}</Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 30, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {card.used}
                  <Box component="em" sx={{ fontStyle: 'normal', fontFamily: DOCUMENT.sans, fontSize: 13, color: DOCUMENT.ink3, ml: 0.5 }}>
                    {t('partnerProfile.ofLimit', { limit: formatPlanLimit(card.limit, t('partnerPlans.unlimited')) })}
                  </Box>
                </Typography>
                <Box sx={{ height: '2px', bgcolor: DOCUMENT.ruleHard, mt: 1.6, position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${card.percent}%`, bgcolor: DOCUMENT.accent }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Section>

        <Section
          title={t('partnerProfile.team')}
          action={t('partnerProfile.inviteMember')}
          onAction={() => {
            if (canInvitePartnerTeamMember(entitlements) === false) {
              setPlanWallReason('team');
              setPlanWallOpen(true);
              return;
            }
            navigate('/partner/team');
          }}
        >
          {team.length === 0 ? (
            <Typography sx={{ py: 3, fontSize: 13.5, color: DOCUMENT.ink3 }}>{t('partnerProfile.noTeam')}</Typography>
          ) : team.map((user) => (
            <Box
              key={user.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', md: '1fr 200px 130px auto' },
                gap: { xs: '6px 16px', md: '20px' },
                alignItems: 'center',
                py: 1.85,
                borderBottom: `1px solid ${DOCUMENT.rule}`,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 400, fontSize: 14.5 }}>{user.name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, mt: 0.35 }}>{user.email}</Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, gridColumn: { xs: '1', md: 'auto' } }}>
                {t(`partnerProfile.roles.${user.role}`, user.role)}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, gridColumn: { xs: '1', md: 'auto' } }}>
                {lastSeenLabel(user)}
              </Typography>
              <Box component="button" onClick={() => navigate('/partner/team')} sx={linkSx}>
                {t('partnerProfile.manage')}
              </Box>
            </Box>
          ))}
        </Section>

        <Box sx={{ mt: 5.75, pt: 2.5, borderTop: `1px solid ${DOCUMENT.ruleHard}` }}>
          <Box
            component="button"
            onClick={() => setCloseDialogOpen(true)}
            sx={{
              ...linkSx,
              color: '#8C2B22',
              '&:hover': { color: '#8C2B22', borderBottomColor: '#8C2B22' },
            }}
          >
            {t('partnerProfile.closeAccount')}
          </Box>
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.85, maxWidth: '60ch', lineHeight: 1.6 }}>
            {t('partnerProfile.closeAccountHelp')}
          </Typography>
        </Box>

        <PartnerWorkspaceFooter supportHref={`mailto:${SUPPORT_MAIL}`} />
      </Box>

      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        onSuccess={() => {
          setPlanWallOpen(false);
          fetchPartnerPlan();
        }}
        reason={planWallReason}
        entitlements={entitlements}
      />

      <Dialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: DOCUMENT.surface, borderRadius: '2px' } }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: 26, color: DOCUMENT.ink }}>
          {t('partnerProfile.closeAccountTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink2, lineHeight: 1.65 }}>
            {t('partnerProfile.closeAccountBody')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCloseDialogOpen(false)} sx={linkSx}>
            {t('partnerProfile.cancel')}
          </Button>
          <Button
            href={`mailto:${SUPPORT_MAIL}?subject=${encodeURIComponent(t('partnerProfile.closeAccount'))}`}
            sx={{
              bgcolor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              borderRadius: 0,
              px: 2,
              '&:hover': { bgcolor: DOCUMENT.nav },
            }}
          >
            {t('partnerProfile.contactSupport')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 22 }}>
          {t('partnerProfile.editTitle')}
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
            {editSuccess && <Alert severity="success" sx={{ mb: 2 }}>{editSuccess}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('partnerProfile.companyName')}
                  name="companyName"
                  value={editFormData.companyName}
                  onChange={handleEditChange}
                  required
                  disabled={editLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('partnerProfile.contactName')}
                  name="contactName"
                  value={editFormData.contactName}
                  onChange={handleEditChange}
                  required
                  disabled={editLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <SingleImageUploader
                  value={editFormData.avatar}
                  onChange={(url) => setEditFormData((prev) => ({ ...prev, avatar: url || '' }))}
                  preset="thumbnail"
                  folder="partners/avatars"
                  label={t('partnerProfile.profilePhoto')}
                  height={112}
                  cropAspect={1}
                  cropShape="round"
                  usePartnerAuth
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('partnerProfile.profilePhotoHint')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('partnerProfile.email')}
                  value={partner?.email || ''}
                  disabled
                  helperText={t('partnerProfile.emailReadonly')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('partnerProfile.phoneNumber')}
                  name="phoneNumber"
                  value={editFormData.phoneNumber}
                  onChange={handleEditChange}
                  required
                  disabled={editLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('partnerProfile.websiteUrl')}
                  name="website"
                  type="url"
                  value={editFormData.website}
                  onChange={handleEditChange}
                  disabled={editLoading}
                  placeholder="https://www.example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('partnerProfile.companyAddress')}
                  name="companyAddress"
                  value={editFormData.companyAddress}
                  onChange={handleEditChange}
                  required
                  multiline
                  rows={2}
                  disabled={editLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('partnerProfile.businessTypeLabel')}
                  name="businessType"
                  value={editFormData.businessType}
                  onChange={handleEditChange}
                  required
                  disabled={editLoading}
                >
                  {['hotel', 'restaurant', 'tour_operator', 'transport', 'activity_provider', 'travel_agency', 'other'].map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`partnerDash.business.${type}`, type)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {editFormData.businessType === 'other' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('partnerProfile.specify')}
                    name="businessTypeOther"
                    value={editFormData.businessTypeOther}
                    onChange={handleEditChange}
                    required
                    disabled={editLoading}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleEditClose} disabled={editLoading}>
              {t('partnerProfile.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={editLoading}
              sx={{ bgcolor: DOCUMENT.oxblood, '&:hover': { bgcolor: DOCUMENT.nav } }}
            >
              {editLoading ? <CircularProgress size={24} /> : t('partnerProfile.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

const Section = ({ title, action, onAction, children }) => (
  <Box sx={{ mt: 5.25 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1.75,
        pb: 1.35,
        borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
      }}
    >
      <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 21, fontWeight: 400 }}>
        {title}
      </Typography>
      {action && (
        <Box component="button" onClick={onAction} sx={{ ...linkSx, ml: 'auto' }}>
          {action}
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

const Fields = ({ children }) => (
  <Box
    component="dl"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      columnGap: { sm: '46px' },
      m: 0,
    }}
  >
    {children}
  </Box>
);

const ThemePreview = ({ form }) => {
  const { t } = useTranslation();
  const banner = /^#[0-9a-fA-F]{6}$/.test(form.brandBanner) ? form.brandBanner : '#000000';
  const menu = /^#[0-9a-fA-F]{6}$/.test(form.brandNavInk) ? form.brandNavInk : '#F6EFE4';
  const ink = /^#[0-9a-fA-F]{6}$/.test(form.brandInk) ? form.brandInk : '#000000';
  const canvas = /^#[0-9a-fA-F]{6}$/.test(form.brandCanvas) ? form.brandCanvas : '#FFFFFF';
  const accent = /^#[0-9a-fA-F]{6}$/.test(form.brandColor) ? form.brandColor : '#C78E33';

  return (
    <Box
      data-testid="partner-theme-preview"
      sx={{ border: `1px solid ${DOCUMENT.ruleHard}` }}
    >
      <Box
        sx={{
          bgcolor: banner,
          color: menu,
          px: 2,
          py: 1.5,
          minHeight: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {form.logo ? (
          <Box
            component="img"
            src={form.logo}
            alt=""
            data-testid="partner-theme-preview-logo"
            sx={{
              height: 40,
              width: 'auto',
              maxWidth: 160,
              objectFit: 'contain',
              objectPosition: 'left center',
              display: 'block',
            }}
          />
        ) : (
          <Typography sx={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
            {t('partnerNav.dashboard')}
          </Typography>
        )}
        <Box sx={{ bgcolor: accent, color: banner, px: 1.25, py: 0.5, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          {t('partnerNav.newTrip')}
        </Box>
      </Box>
      <Box sx={{ bgcolor: canvas, color: ink, px: 2, py: 1.75 }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, fontWeight: 400 }}>
          {t('partnerNav.packages')}
        </Typography>
      </Box>
    </Box>
  );
};

const Swatch = ({ value, empty }) => {
  if (!value) return empty;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 18, height: 18, bgcolor: value, border: `1px solid ${DOCUMENT.ruleHard}` }} />
      {value}
    </Box>
  );
};

const ColorField = ({ name, label, hint, value, fallback, disabled, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    <Box
      component="input"
      type="color"
      name={name}
      value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
      sx={{
        width: 48,
        height: 48,
        p: 0,
        border: `1px solid ${DOCUMENT.ruleHard}`,
        bgcolor: 'transparent',
        cursor: 'pointer',
      }}
    />
    <TextField
      fullWidth
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={fallback}
      helperText={hint}
    />
  </Box>
);

export default PartnerProfile;
